import React from 'react';
import { TextField, Divider } from "@mui/material";
import { Box } from "@mui/system";
import dayjs from 'dayjs';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import NCRFormHeading from '../NCRFormHeading';
import NCRFormLabel from '../NCRFormLabel';
import NCRSelectField from '../NCRSelectField';
import NCRTextField from '../NCRTextField';

export default function GeneralInformationBox({ content, onChange }) {

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    onChange({ [name]: value });
  };

  const theme = createTheme({
    components: {
      MuiInputBase: {
        styleOverrides: {
          input: {
            "&::placeholder": {
              fontSize: "14px",
              fontWeight: 400,
              color: "rgba(84, 84, 84, 1)",
              opacity: 1,
            },
          },
        },
      },
    },
  });

  return (
    <>
      <ThemeProvider theme={theme}>
        <Box>
          <Box sx={{ p: 3 }}>
            <Box>
              <NCRFormHeading isRequired={true} tooltipData={
                [
                  { title: "NC ID", description: "A unique identification number for each NCR." },
                  { title: "Date of Issue", description: "The date when the non-conformance is identified." },
                  { title: "Department/Function", description: "Dropdown options for departments involved in the process." },
                  { title: "Reported by", description: "Name of the person who raised the NCR." },
                  { title: "NC Type", description: "Dropdown options to classify the type of non-conformance." },
                  { title: "Location of NC", description: "Dropdown or text field to capture where the non-conformance was observed." }
                ]
              }>
                General Information
              </NCRFormHeading>
            </Box>
            <div style={{ display: 'flex' }}>
              <div style={{ width: '35%', paddingRight: '77px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <NCRFormLabel>NC ID:</NCRFormLabel>
                  <TextField
                    size='small'
                    sx={{
                      width: '200px',
                    }}
                    name="ncr_id"
                    onChange={handleInputChange}
                    value={content.formData.ncr_id || ''}
                    disabled
                    placeholder="Enter NC ID"
                  />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <NCRFormLabel>Reported by:</NCRFormLabel>
                  <NCRTextField
                    name="reported_by"
                    onChange={handleInputChange}
                    disabled={content.inputDisable}
                    value={content.formData.reported_by || ''}
                    placeholder="Enter Reported by"
                    error={!!content?.errors?.reported_by}
                    maxLength={250}
                  />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <NCRFormLabel>Location of NC:</NCRFormLabel>
                  <NCRSelectField
                    name="location"
                    value={content.formData.location}
                    onChange={handleInputChange}
                    options={content.lookupTypes?.location || []}
                    disabled={content.inputDisable}
                    error={!!content?.errors?.location}
                    placeholder="Select option"
                  />
                </div>
              </div>
              <div style={{ width: '35%', paddingRight: '82px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <NCRFormLabel>Date of issue:</NCRFormLabel>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DesktopDatePicker
                      defaultValue={dayjs()}
                      name="dateRange"
                      disabled
                      value={content.formData.date_of_issue ? dayjs(content.formData.date_of_issue) : dayjs()}
                      format="DD/MM/YYYY"
                      renderInput={(params) => <TextField {...params} />}
                      sx={{
                        "& .MuiInputBase-root": {
                          height: "42px",
                          width: '200px',
                          fontSize: "14px",
                        },
                        "& .MuiSvgIcon-root": {
                          fontSize: "18px",
                        },
                      }}
                    />
                  </LocalizationProvider>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <NCRFormLabel>NC type:</NCRFormLabel>
                  <NCRSelectField
                    name="ncr_type"
                    value={content.formData.ncr_type}
                    onChange={handleInputChange}
                    options={content.lookupTypes?.ncrTypes || []}
                    disabled={content.inputDisable}
                    error={!!content?.errors?.ncr_type}
                  />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <NCRFormLabel>NC Category:</NCRFormLabel>
                  <NCRSelectField
                    name="category"
                    value={content.formData.category}
                    onChange={handleInputChange}
                    options={content.lookupTypes?.category || []}
                    disabled={content.inputDisable}
                    error={!!content?.errors?.category}
                  />
                </div>
              </div>
              <div style={{ width: '30%', paddingRight:'12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <NCRFormLabel>Department/ Function:</NCRFormLabel>
                  <NCRSelectField
                    name="department"
                    value={content.formData.department}
                    onChange={handleInputChange}
                    options={content.lookupTypes?.departments || []}
                    disabled={content.inputDisable}
                    error={!!content?.errors?.department}
                  />
                </div>
                {content.formData.department === 'Other' &&
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <NCRFormLabel>Department/ Function:</NCRFormLabel>
                    <NCRTextField
                      maxLength={250}
                      size='small'
                      sx={{
                        width: '200px',
                      }}
                      multiline
                      name="department_other"
                      onChange={handleInputChange}
                      disabled={content.inputDisable}
                      value={content.formData.department_other || ''}
                      placeholder="Enter department"
                      error={!!content?.errors?.department_other}
                    />
                  </div>
                }
              </div>
            </div>
          </Box>
          <Divider sx={{ mb: 1 }} />
        </Box>
      </ThemeProvider >
    </>
  );
};
